/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { typography2 } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';

import type { Maybe } from 'bundles/tutorials/__generated__/baseContentfulTypes';
import TutorialMarkdown from 'bundles/tutorials/components/TutorialMarkdown';

type Props = {
  bodyText?: Maybe<string>;
};

const styles = {
  body: (theme: Theme) => css`
    margin-bottom: var(--cds-spacing-600);

    & > div {
      ${theme.typography.h2}
      color: var(--cds-color-neutral-primary);

      table {
        margin: var(--cds-spacing-300) 0;

        @media (max-width: 767px) {
          overflow: scroll;
          height: 300px;
        }

        border-radius: var(--cds-spacing-50);

        ${typography2.bodySecondary}
        th, td {
          border: 0;
          padding-left: var(--cds-spacing-150);
        }

        tr {
          height: 53px;
        }

        thead {
          tr {
            background-color: var(--cds-color-blue-50);
            font-weight: bold;
          }
        }

        tr:nth-child(2n + 2) {
          background-color: var(--cds-color-neutral-background-primary-weak);
        }

        td {
          ul {
            max-width: fit-content;
            padding-right: 45px;

            li {
              text-align: start;
            }
          }
        }
      }

      ol,
      ul {
        padding-left: var(--cds-spacing-600);
        margin-left: 0;
        margin-top: var(--cds-spacing-150);
      }

      ul > ul,
      ol > ol,
      li > ul,
      li > ol {
        margin-top: 0;
      }

      .vjs-menu-content {
        padding-left: 0;
        font-size: inherit;
        margin: 0;
      }

      li {
        font-size: 18px;

        p {
          ${theme.typography.h2}
          margin-top: 0;
          margin-bottom: 0;
        }
      }

      & > p {
        ${theme.typography.h2}
        font-size: 18px;
        margin-top: var(--cds-spacing-300);
        margin-bottom: 0;
      }

      & > h2 {
        ${theme.typography.h1semibold}
        font-size: 30px;
      }

      & > h3 {
        ${theme.typography.h2semibold}
        font-size: 24px
      }

      & > h4 {
        ${theme.typography.h2semibold}
        font-size: 20px;
      }

      & > h2,
      > h3,
      > h4 {
        margin-top: var(--cds-spacing-600);
        margin-bottom: 0;
      }

      p > a {
        color: var(--cds-color-blue-700);
        text-decoration: underline;

        &:hover {
          text-decoration: none;
        }

        &:visited {
          color: var(--cds-color-purple-950);
        }
      }

      b,
      strong {
        ${theme.typography.h2semibold}
        font-size: inherit;
        line-height: inherit;
      }

      figure {
        width: 100%;
        margin-bottom: 0;

        img {
          margin-top: var(--cds-spacing-600);
          width: 100%;
        }
      }

      figcaption {
        color: var(--cds-color-grey-600);
        font-weight: 400;
        font-size: 14px;
        line-height: 20px;
        margin-top: var(--cds-spacing-300);
      }

      blockquote {
        align-items: start;
        margin: var(--cds-spacing-600) 0 0 0;
        border-left: none;
        display: grid;
        position: relative;
        grid-template-columns: auto 90% auto;
        padding: 0 var(--cds-spacing-300) 0 var(--cds-spacing-600);

        &::before {
          content: url('https://d2j5ihb19pt1hq.cloudfront.net/articles/svg/quote-left.svg');
          left: var(--cds-spacing-600);
          position: absolute;
        }

        &::after {
          padding-left: var(--cds-spacing-50);
          content: url('https://d2j5ihb19pt1hq.cloudfront.net/articles/svg/quote-right.svg');
          position: absolute;
          right: 20px;
        }

        p {
          ${theme.typography.h1}
          font-style: italic;
          padding: 0 var(--cds-spacing-100) var(--cds-spacing-100) var(--cds-spacing-100);
          color: var(--cds-color-blue-900);
          grid-column: 2;
          margin-bottom: 0;
          margin-top: 0;

          b,
          strong {
            ${theme.typography.h1semibold}
          }

          @media screen and (max-width: 480px) {
            padding: var(--cds-spacing-300) 0 0 0;
            line-height: 32px;
          }
        }

        @media screen and (max-width: 480px) {
          padding: var(--cds-spacing-300) 0 0 0;

          &::before {
            left: 0;
          }

          &::after {
            right: 0;
          }
        }
      }
    }
  `,
};

const Body = ({ bodyText }: Props) => {
  if (!bodyText) {
    return null;
  }

  return (
    <div className="rc-Body" css={styles.body}>
      <TutorialMarkdown>{bodyText}</TutorialMarkdown>
    </div>
  );
};

export default Body;
